import api from './api';

export interface Gateway {
  id: number;
  company_id: number | null;
  name: string;
  gateway_id: string;
  status: 'active' | 'pending' | 'error' | 'online' | 'offline' | 'inactive';
  endpoint_url?: string;
  provider?: string;
  hostname?: string;
  ip_address?: string;
  health_status?: Record<string, any>;
  last_health_check?: string;
  error_message?: string;
  hasCredentials?: boolean;
  last_heartbeat?: string;
  created_at: string;
  updated_at: string;
  company_name?: string;
}

export interface GatewayListParams {
  page?: number;
  limit?: number;
  company_id?: number;
  status?: string;
}

export interface GatewayListResponse {
  gateways: Gateway[];
  total: number;
  page: number;
  pages: number;
}

export interface UpdateGatewayData {
  name?: string;
  gateway_id?: string;
  status?: 'online' | 'offline' | 'inactive';
  endpointUrl?: string;
  companyId?: string;
  credentials?: {
    apiKey?: string;
    teamId?: string;
  };
}

export interface GatewayApiKey {
  id: number;
  gateway_id: number;
  name: string;
  key_prefix: string;
  // Device restriction: [] = all of the company's devices.
  device_ids?: string[];
  last_used_at?: string;
  expires_at?: string;
  created_at: string;
  is_active: boolean;
}

export interface CreateApiKeyData {
  name: string;
  expiresIn?: string;
  expiresAt?: string;
  // Optional device restriction; omit or [] for the whole fleet.
  device_ids?: string[];
}

export interface CreateApiKeyResponse {
  message: string;
  key: string; // Full key — only returned on creation, store it immediately
  id: number;
  gateway_id: number;
  name: string;
  key_prefix: string;
  expires_at?: string;
  is_active: boolean;
  created_at: string;
}

export async function getGateways(params: GatewayListParams = {}): Promise<GatewayListResponse> {
  const cleanParams: Record<string, any> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') {
      cleanParams[key] = value;
    }
  }
  const response = await api.get<GatewayListResponse>('/gateways', { params: cleanParams });
  return response.data;
}

export async function checkGatewayHealth(id: number): Promise<{ healthy: boolean; message: string }> {
  const response = await api.post<{ healthy: boolean; message: string }>(`/gateways/${id}/health`);
  return response.data;
}

export async function getApiKeys(gatewayId: number): Promise<{ keys: GatewayApiKey[] }> {
  const response = await api.get<{ keys: GatewayApiKey[] }>(`/gateways/${gatewayId}/api-keys`);
  return response.data;
}

export async function createApiKey(gatewayId: number, data: CreateApiKeyData): Promise<CreateApiKeyResponse> {
  const response = await api.post<CreateApiKeyResponse>(`/gateways/${gatewayId}/api-keys`, data);
  return response.data;
}

export async function revokeApiKey(gatewayId: number, keyId: number): Promise<{ message: string }> {
  const response = await api.delete<{ message: string }>(`/gateways/${gatewayId}/api-keys/${keyId}`);
  return response.data;
}

/** Replace a key's device restriction; [] restores whole-fleet access. */
export async function updateApiKeyDevices(gatewayId: number, keyId: number, deviceIds: string[]): Promise<{ key: GatewayApiKey }> {
  const response = await api.put<{ key: GatewayApiKey }>(`/gateways/${gatewayId}/api-keys/${keyId}/devices`, { device_ids: deviceIds });
  return response.data;
}

/**
 * Update gateway details
 * @requires Super Admin role
 */
export async function updateGateway(id: number | string, data: UpdateGatewayData): Promise<Gateway> {
  const response = await api.put<Gateway>(`/gateways/${id}`, data);
  return response.data;
}

export interface CreateGatewayData {
  companyId: string;
  name: string;
  provider: string;
  endpointUrl?: string;
}

export async function createGateway(data: CreateGatewayData): Promise<Gateway> {
  const response = await api.post<Gateway>('/gateways', data);
  return response.data;
}

export async function deleteGateway(id: number | string): Promise<void> {
  await api.delete(`/gateways/${id}`);
}

export async function getGatewaysByCompany(companyId: string | number): Promise<Gateway[]> {
  const response = await api.get<{ gateways: Gateway[] }>('/gateways', { params: { companyId } });
  return response.data.gateways;
}

export async function getUnclaimedGateways(): Promise<Gateway[]> {
  const response = await api.get<{ gateways: Gateway[] }>('/gateways', { params: { claimed: 'false' } });
  return response.data.gateways;
}

export default {
  getGateways,
  checkGatewayHealth,
  getApiKeys,
  createApiKey,
  revokeApiKey,
  updateGateway,
  createGateway,
  getGatewaysByCompany,
  getUnclaimedGateways,
};
